import Link from "next/link";
import type { ComponentProps } from "react";
import React from "react";
import { toast } from "react-toastify";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { DealLayout } from "~/components/common/layouts/deals/DealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ReportImagesArea } from "~/components/common/reports/ReportImagesArea";
import { ReportImagesAreaSp } from "~/components/common/reports/ReportImagesAreaSp";
import { ReportImageUploadPanel } from "~/components/common/reports/ReportImageUploadPanel";
import { useDealCompletionReportImages } from "~/hooks/deals/useDealCompletionReportImages";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import type { NskResponse } from "~/models/Api";
import type {
	DealReportBulkUpdateParams,
	DealReportImage
} from "~/models/DealReportImage";
import {
	NUMBER_OF_DELETE_AT_ONCE,
	NUMBER_OF_UPLOAD_AT_ONCE
} from "~/models/DealReportImage";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getDownloadPdfPath } from "~/utils/api/deals/DealCompletionReport";
import {
	bulkDelete,
	bulkUpdate,
	destroy,
	getDownloadPath as getDownloadImagePath,
	getDownloadZipPath,
	create as postCompletionReportImages,
	update,
	updateSeq
} from "~/utils/api/deals/DealCompletionReportImage";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const id = useIdQueryHandler();
	const [uploading, setUploading] = React.useState<boolean>(false);
	const [deleting, setDeleting] = React.useState<boolean>(false);
	const [progress, setProgress] = React.useState<number>(0);
	const [selectedImageIds, setSelectedImageIds] = React.useState<
		readonly number[]
	>([]);
	const { data: deal } = useDeals().show(id);
	// globalMutateしたい
	const { data: reportImages, mutate: mutateReportImages } =
		useDealCompletionReportImages().index(id);
	if (deal === null || reportImages === null) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "完了報告画像" }
	] as const satisfies ComponentProps<typeof DealLayout>["items"];
	const handleDrop = async (files: File[]): Promise<void> => {
		setProgress(0);
		setUploading(true);
		const arrFiles: File[][] = [];
		const totalFiles: number = files.length;
		let finishedFiles = 0;
		while (files.length > 0) {
			arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
		}
		for await (const sendFiles of arrFiles) {
			const result = await postCompletionReportImages(sendFiles, deal.id);
			if (isSuccess(result)) {
				await mutateReportImages?.();
			} else {
				toast.error("エラーが発生しました");
				return;
			}
			finishedFiles += sendFiles.length;
			setProgress(Math.round((finishedFiles / totalFiles) * 100));
			await mutateReportImages?.();
		}
		setUploading(false);
		toast.success("画像を登録しました");
	};
	const handleDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, undefined);
		window.open(path);
	};
	const handleDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id);
		window.open(path);
	};
	const handleSelectedDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id, selectedImageIds);
		window.open(path);
	};
	const handleSelectedDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, selectedImageIds);
		window.open(path);
	};
	const handleDownloadImage = (reportImage: DealReportImage): void => {
		const path = getDownloadImagePath(reportImage);
		window.open(path);
	};
	const handleUpdate = async (
		submitValue: DealReportImage
	): Promise<NskResponse> => update(submitValue);
	const handleBulkUpdate = async (
		submitValue: DealReportBulkUpdateParams
	): Promise<void> => {
		const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
		if (isSuccess(result)) {
			await mutateReportImages?.();
			toast.success("更新しました");
		} else {
			toast.error("エラーが発生しました");
		}
	};
	const handleDestroy = async (
		reportImage: DealReportImage
	): Promise<NskResponse> => destroy(reportImage);
	const handleBulkDelete = async (): Promise<void> => {
		if (selectedImageIds.length === 0) {
			return;
		}
		setProgress(0);
		setDeleting(true);
		const imageIdArray: number[][] = [];
		const totalFiles: number = selectedImageIds.length;
		let finishedFiles = 0;
		for (
			let i = 0, j = selectedImageIds.length;
			i < j;
			i += NUMBER_OF_DELETE_AT_ONCE
		) {
			imageIdArray.push(
				selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
			);
		}
		for await (const ids of imageIdArray) {
			const result = await bulkDelete(deal.id, ids);
			if (isSuccess(result)) {
				await mutateReportImages?.();
			} else {
				toast.error("エラーが発生しました");
				return;
			}
			finishedFiles += ids.length;
			setProgress(Math.round((finishedFiles / totalFiles) * 100));
			await mutateReportImages?.();
		}
		setDeleting(false);
		setSelectedImageIds([]);
		toast.success("画像を削除しました");
	};
	const handleUpdateSeq = async (
		newOrderItems: DealReportImage[]
	): Promise<void> => {
		updateSeq(newOrderItems, Number(id));
	};
	const onChangeSelectedImage = (selectedImageId: number): void => {
		if (selectedImageIds.includes(selectedImageId)) {
			setSelectedImageIds(
				selectedImageIds.filter((imageId) => imageId !== selectedImageId)
			);
		} else {
			setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
		}
	};
	const handleResetSelectedIds = (): void => {
		setSelectedImageIds([]);
	};
	const handleSelectAllImages = (): void => {
		setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
	};
	return (
		<DefaultV2>
			<DealLayout deal={deal} isVendor={false} items={items} noBackground>
				<div className={Styles.MainWrapper}>
					<h2 className={Styles.ReportTitle}>完了報告画像の登録</h2>
					<ReportImageUploadPanel
						deleting={deleting}
						handleDrop={handleDrop}
						progress={progress}
						uploading={uploading}
					/>
					<ReportImagesArea
						companyType="company"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
					<ReportImagesAreaSp
						companyType="company"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
				</div>
			</DealLayout>
		</DefaultV2>
	);
};
